import { t } from "@lingui/macro";
import {
  type OutgoingInvoiceForNavigationQuery,
  OutgoingInvoiceTypeEnum,
  useOutgoingInvoiceForNavigationQuery,
} from "@src/__generated__/graphql";
import { useNavigationPages } from "@src/components/widgets/AppSubNav/hooks";
import type { NavigationHook } from "@src/components/widgets/AppSubNav/hooks/types";

function getInvoiceTitlePrefix(
  type: OutgoingInvoiceTypeEnum | undefined,
): string {
  switch (type) {
    case OutgoingInvoiceTypeEnum.Invoice:
      return t`Outgoing invoice`;
    case OutgoingInvoiceTypeEnum.CreditNote:
      return t`Credit note`;
    case OutgoingInvoiceTypeEnum.ProformaPayment:
      return t`Payment receipt`;
    default:
      return t`Invoice`;
  }
}

export function useOutgoingInvoiceNavigation(
  invoiceId?: string,
): NavigationHook<OutgoingInvoiceForNavigationQuery["outgoingInvoice"]> {
  const { data, loading } = useOutgoingInvoiceForNavigationQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: invoiceId ?? "",
    },
    skip: !invoiceId,
  });
  const invoiceTitlePrefix = getInvoiceTitlePrefix(data?.outgoingInvoice?.type);

  const { activePage, activeSubpage, allPages, visiblePages } =
    useNavigationPages([
      {
        title: () => t`Outgoing invoices`,
        href: {
          pathname: "/invoices/outgoing-invoices",
        },
        subPages: [
          {
            title: () =>
              data?.outgoingInvoice?.invoice_no
                ? `${invoiceTitlePrefix} #${data.outgoingInvoice.invoice_no}`
                : t`Invoice detail`,
            href: {
              pathname: "/invoices/outgoing-invoices/detail",
              query: {
                id: invoiceId,
              },
            },
          },
          {
            title: () => t`Edit`,
            href: {
              pathname: "/invoices/outgoing-invoices/edit",
              query: {
                id: invoiceId,
              },
            },
          },
          {
            title: () =>
              data?.outgoingInvoice?.type ===
              OutgoingInvoiceTypeEnum.ProformaPayment
                ? t`New request for proforma invoice`
                : data?.outgoingInvoice?.type ===
                    OutgoingInvoiceTypeEnum.CreditNote
                  ? t`New credit note`
                  : t`New request for invoice`,
            href: {
              pathname: "/invoices/outgoing-invoices/duplicate",
              query: {
                id: invoiceId,
              },
            },
          },
        ],
      },
    ]);

  return {
    activePage,
    activeSubpage,
    allPages,
    visiblePages,
    data: data?.outgoingInvoice,
    isLoading: loading,
  };
}
